import { NON_CONTINGENT } from './constants';
import {
    handlePremiumSettingsTableData,
    calculateVerticalHeaders,
    calculateOuterVerticalHeaders,
    calculateGroupColumns,
    getTableHeaderData,
} from './premiumSettingsFunctions';
import { readonlyPremiumSettingsTableColumns } from './utilFunctions';

const normalize = (arr) => (arr || []).map(s => ({
    ...s,
    isNonContingent: s.premiumType === NON_CONTINGENT,
}));

export const buildRevisionTableProps = (
    revision = null, isPercentagePremium = false, showTable, isApplicationPremium,
    damages, selectedCaseType, showNonContingentToggle, topHeader, currentData = null
) => {
    if (!revision) return null;
    const {
        selectedCaseStages,
        selectedNonContingentCaseStages,
        applicationPremiumSubSettingsDtos,
    } = revision;

    const revisionDamageBands = revision.damageBands ?? [];
    const headerData = getTableHeaderData(revisionDamageBands, damages);
    const verticalHeaders = calculateVerticalHeaders(selectedCaseStages, selectedNonContingentCaseStages);
    // GPL - deliberately NOT passing isUnderwriterPremiumLocked here. Every other surface renders the
    // Underwriter Rate/Amount cells zeroed for a Green/LEI group, but this is the revision-history
    // modal: a revision is an audit snapshot of what was actually saved, and must always render that
    // persisted value verbatim - even if a future bug or bypass ever let a non-zero value through, this
    // view exists to surface exactly that, not paper over it. Do not "fix" this by wiring the flag in.
    const columns = readonlyPremiumSettingsTableColumns(headerData, isPercentagePremium, showTable, isApplicationPremium);
    const groups = calculateGroupColumns(columns);
    const normalizedDtos = normalize(applicationPremiumSubSettingsDtos);
    const data = handlePremiumSettingsTableData(normalizedDtos, selectedCaseType, isPercentagePremium, showTable, isApplicationPremium);
    const outerVerticalHeaders = showNonContingentToggle ? calculateOuterVerticalHeaders(selectedCaseStages, selectedNonContingentCaseStages) : null;

    return {
        columns,
        verticalHeaders,
        groups,
        data,
        outerVerticalHeaders,
        topHeader,
    };
};

export const handleAnnotatedPremiumSettingsTableData = (
    premiumSettingsData, historyItemsData, annotationField,
    selectedCaseType, isPercentagePremium, showTable, isApplicationPremium
) => {
    // Build display-ready table rows from the current revision's DTOs
    const tableData = handlePremiumSettingsTableData(normalize(premiumSettingsData), selectedCaseType, isPercentagePremium, showTable, isApplicationPremium);
    // No previous revision to diff against — return unannotated (nothing to highlight)
    if (!historyItemsData || historyItemsData.length === 0) return tableData;
    // Build display-ready rows from the previous revision's DTOs for diffing
    const compareData = handlePremiumSettingsTableData(normalize(historyItemsData), selectedCaseType, isPercentagePremium, showTable, isApplicationPremium);
    // Unique key per row: stage + name(s) + contingency flag + solicitor (for commission rows) — used to match rows across revisions
    const rowKey = (row) => {
        const nameKey = Array.isArray(row.name) ? row.name.join(',') : row.name;
        const solicitorKey = row.solicitorId != null ? `-sol${row.solicitorId}` : '';
        return `${row.stageId}-${nameKey}-${row.isNonContingent}${solicitorKey}`;
    };
    // Index previous rows by key for O(1) lookup during the diff
    const compareMap = new Map(compareData.map(row => [rowKey(row), row]));
    const hasMeaningfulValue = (cell) =>
        convertToNumericString(cell.amount) !== '' || convertToNumericString(cell.percentage) !== '';
    return tableData.map(row => {
        // Find the matching row from the previous revision by composite key
        const compareRow = compareMap.get(rowKey(row));
        if (!compareRow) {
            const newColumns = row.data.reduce((acc, cell, i) => {
                if (hasMeaningfulValue(cell)) acc.push(`premiumCharge${i}`);
                return acc;
            }, []);
            return newColumns.length > 0 ? { ...row, [annotationField]: newColumns } : row;
        }
        const changedFields = row.data.reduce((acc, cell, colIndex) => {
            const compareCell = compareRow.data[colIndex];
            if (!compareCell) {
                if (hasMeaningfulValue(cell)) acc.push(`premiumCharge${colIndex}`);
            } else if (
                convertToNumericString(cell.amount) !== convertToNumericString(compareCell.amount) ||
                convertToNumericString(cell.percentage) !== convertToNumericString(compareCell.percentage)
            ) {
                acc.push(`premiumCharge${colIndex}`);
            }
            return acc;
        }, []);
        return changedFields.length > 0 ? { ...row, [annotationField]: changedFields } : row;
    });
};

// For text fields: null and '' are both "empty", normalize to the same value
export const convertToString = (val) => val != null ? String(val) : '';

export const convertToNumericString = (val) => {
    if (val == null || val === '') return '';
    return String(val);
}

export const isRevised = (currentValue, previousValue) =>
    currentValue !== undefined && convertToString(currentValue) !== convertToString(previousValue);

export const isNumericRevised = (currentValue, previousValue) =>
        currentValue !== undefined && convertToNumericString(currentValue) !== convertToNumericString(previousValue);

export const applicationPremiumSettingsShowTableFields = (currentRevision, previousRevision) => {
    const current = currentRevision ?? {};
    const previous = previousRevision ?? {};

    return {
        limitOfIndemnity: isNumericRevised(current.limitOfIndemnity, previous.limitOfIndemnity),
        recoverableRatePercentage: isNumericRevised(current.recoverableRatePercentage, previous.recoverableRatePercentage),
        comments: isRevised(current.comments, previous.comments),
        notes: isRevised(current.notes, previous.notes),
        endorsements: isRevised(current.endorsements, previous.endorsements),
        displayedCoverageType: isRevised(current.coverageOptionId, previous.coverageOptionId),
        displayedDeductible: isRevised(current.deductibleOptionId, previous.deductibleOptionId),
        displayedEndorsement: isRevised(current.endorsementOptionId, previous.endorsementOptionId),
    };
};


export const getFieldStyles = (field, emphasizedFields, highlightedFields) => {
    const isEmphasized = Array.isArray(emphasizedFields) ? emphasizedFields.includes(field) : !!emphasizedFields?.[field];
    if (isEmphasized) return 'emphasized-style';
    const isHighlighted = Array.isArray(highlightedFields) ? highlightedFields.includes(field) : !!highlightedFields?.[field];
    if (isHighlighted) return 'highlighted-style';
    return '';
};

export const stagedPremiumSettingsShowTableFields = (currentRevision, previousRevision) => {
    const current = currentRevision ?? {};
    const previous = previousRevision ?? {};
    const currentStages = current.applicationStagedPremiumSubSettingDtos || [];
    const previousStages = previous.applicationStagedPremiumSubSettingDtos || [];
    const previousStageMap = new Map(previousStages.map(s => [s.stageMachineName, s]));

    const stages = {};
    currentStages.forEach(stage => {
        const prev = previousStageMap.get(stage.stageMachineName) || {};
        const prevCommissionMap = new Map(
            (prev.stagedPremiumSubSettingCommissionDtos || []).map(c => [c.lawsysId, c])
        );
        const commissionFields = {};
        (stage.stagedPremiumSubSettingCommissionDtos || []).forEach(c => {
            const prevC = prevCommissionMap.get(c.lawsysId) || {};
            commissionFields[`commission_${c.lawsysId}`] = isNumericRevised(c.commissionPercentage, prevC.commissionPercentage);
        });
        stages[stage.stageMachineName] = {
            premiumAmount: isNumericRevised(stage.premiumAmount, prev.premiumAmount),
            limitOfIndemnity: isNumericRevised(stage.limitOfIndemnity, prev.limitOfIndemnity),
            underwriterRatePercentage: isNumericRevised(stage.underwriterRatePercentage, prev.underwriterRatePercentage),
            ...commissionFields,
        };
    });

    return {
        notes: isRevised(current.notes, previous.notes),
        comments: isRevised(current.comments, previous.comments),
        displayedCoverageType: isRevised(current.coverageOptionId, previous.coverageOptionId),
        displayedDeductible: isRevised(current.deductibleOptionId, previous.deductibleOptionId),
        displayedEndorsement: isRevised(current.endorsementOptionId, previous.endorsementOptionId),
        averageExpectedPremium: isNumericRevised(current.averageExpectedPremium, previous.averageExpectedPremium),
        stages,
    };
};